import { useQuery } from '@tanstack/react-query';
import { Valor } from '../../components/Valor';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../app/auth-context';
import { CategoryTag } from '../../components/CategoryTag';
import { Rosca } from '../../components/Rosca';
import { LeituraDoMes } from './LeituraDoMes';
import { Projecao } from './Projecao';
import { SaldoPorBanco } from './SaldoPorBanco';
import { api } from '../../lib/api';
import {
  absMoney,
  amountClass,
  currentMonth,
  dateBR,
  money,
  monthLabel,
  shiftMonth,
} from '../../lib/format';

/**
 * Dashboard do mes.
 *
 * Os numeros aqui obedecem as regras 5 e 6: transferencia entre contas e
 * pagamento de fatura nao aparecem como receita nem como despesa. O gasto do
 * mes e o das compras, contado uma vez so.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const navegar = useNavigate();
  const [month, setMonth] = useState(currentMonth());
  /** `categoryId` da fatia aberta. Null quando nenhuma esta. */
  const [fatia, setFatia] = useState<string | null>(null);

  const dashboard = useQuery({
    queryKey: ['dashboard', month],
    queryFn: () => api.dashboard(month),
  });

  const payables = useQuery({
    queryKey: ['payables-summary'],
    queryFn: () => api.payablesSummary(),
  });

  const compromissos = useQuery({
    queryKey: ['compromissos', month],
    queryFn: () => api.compromissos(month),
  });

  /**
   * A lista que abre ao clicar numa fatia.
   *
   * Nao ha aba separada para isto: "para quem eu pago" e uma pergunta sobre
   * uma categoria, nao um assunto proprio, e vive dentro do grafico. Sem
   * fatia escolhida, traz o mes inteiro.
   */
  const detalhe = useQuery({
    queryKey: ['payees', month, fatia],
    queryFn: () => api.payees(month, fatia ?? undefined),
  });

  const data = dashboard.data;

  return (
    <div className="main">
      <header className="page-header">
        <div className="between">
          <div>
            <h1>
              {saudacao()}, {user?.name.split(' ')[0]}.
            </h1>
            <p>{monthLabel(month)}</p>
          </div>
          <div className="row">
            <button className="btn ghost" onClick={() => setMonth(shiftMonth(month, -1))}>
              Mes anterior
            </button>
            <button
              className="btn ghost"
              onClick={() => setMonth(shiftMonth(month, 1))}
              disabled={month >= currentMonth()}
            >
              Proximo mes
            </button>
          </div>
        </div>
      </header>

      {dashboard.isLoading && <p className="muted">Carregando...</p>}

      {data && (
        <>
          {/* Todo cartao leva a algum lugar: numero que nao pode ser aberto
              e numero que nao da para conferir. */}
          <div className="grid">
            <Cartao rotulo="Disponivel" para="/contas" destaque>
              <Valor cents={data.totalBalanceCents} className="stat-value" />
            </Cartao>
            <Cartao rotulo="Patrimonio liquido" para="/patrimonio">
              <Valor cents={data.netWorthCents} className="stat-value" />
            </Cartao>
            <Cartao rotulo="Receitas do mes" para="/lancamentos?kind=INCOME">
              <Valor cents={data.monthIncomeCents} className="stat-value" />
            </Cartao>
            <Cartao rotulo="Despesas do mes" para="/lancamentos?kind=EXPENSE">
              <Valor cents={data.monthExpenseCents} saida className="stat-value" />
            </Cartao>
          </div>

          <div className="grid" style={{ marginTop: 'var(--space-4)' }}>
            {/*
              "Disponivel" responde quanto ha na conta; este responde quanto
              disso e seu de verdade. Sao perguntas diferentes, e a segunda e
              a que decide se da para gastar hoje.
            */}
            {compromissos.data && (
              <Cartao rotulo="Livre para usar" para="/a-pagar">
                <Valor cents={compromissos.data.sobraCents} className="stat-value" />
                <span className="card-hint" style={{ display: 'block', margin: 0 }}>
                  ja descontado o que falta pagar e a fatura
                </span>
              </Cartao>
            )}
            <article className="stat">
              <div className="stat-label">Resultado do mes</div>
              <Valor cents={data.monthNetCents} className="stat-value" />
            </article>
            <article className="stat">
              <div className="stat-label">Fatura de cartao em aberto</div>
              <div className="stat-value">
                <Valor cents={data.creditCardDebtCents} saida />
              </div>
            </article>
          </div>

          <div className="stack" style={{ marginTop: 'var(--space-6)' }}>
            {/* Antes das contas a pagar: e o que da sentido aos numeros de
                cima, e no fim da pagina ninguem chegaria nele. */}
            <LeituraDoMes month={month} />

            {/* Logo abaixo do "Disponivel", que e a soma disto: quem olha o
                total costuma querer saber, em seguida, de qual banco sai. */}
            <SaldoPorBanco />

            {/* Passado, presente, futuro — nesta ordem. A leitura fecha o mes
                que foi, o saldo por banco diz onde o dinheiro esta agora, e
                aqui comeca a unica parte sobre a qual ainda da para agir. */}
            <Projecao />

            {payables.data && payables.data.next.length > 0 && (
              <section className="card">
                <h2 className="card-title">Contas a pagar</h2>
                <p className="card-hint">
                  {payables.data.overdueCount > 0
                    ? `${payables.data.overdueCount} atrasada(s), somando ${money(payables.data.overdueCents)}.`
                    : 'Nada atrasado. O que vence nos proximos dias:'}
                </p>

                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Vencimento</th>
                        <th>Conta</th>
                        <th style={{ textAlign: 'right' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payables.data.next.map((payable) => (
                        <tr key={payable.id}>
                          <td className="tabular">{dateBR(payable.dueDate)}</td>
                          <td className={payable.overdue ? 'expense' : undefined}>
                            {payable.description}
                            {payable.overdue && ' (atrasada)'}
                          </td>
                          <td className="money" style={{ textAlign: 'right' }}>
                            {money(payable.amountCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Link to="/a-pagar" style={{ fontSize: 'var(--text-sm)' }}>
                  Ver todas
                </Link>
              </section>
            )}

            {/* A rosca, e o que ela abre. Clicar numa fatia mostra para quem
                o dinheiro daquela categoria foi. Nao ha aba separada para
                isso: e uma pergunta sobre a categoria, nao um assunto. */}
            <section className="card">
              <h2 className="card-title">No que eu gasto</h2>
              <p className="card-hint">
                Saidas de {monthLabel(month).toLowerCase()}, por categoria. Clique numa fatia para
                ver para quem o dinheiro foi.
              </p>

              <Rosca
                fatias={data.topCategories.map((categoria) => ({
                  key: categoria.categoryId ?? categoria.name,
                  label: categoria.name,
                  colorToken: categoria.colorToken,
                  amountCents: categoria.totalCents,
                  share: categoria.share,
                }))}
                selecionada={fatia}
                onSelecionar={setFatia}
                centro={money(data.monthExpenseCents)}
                rotuloDoCentro="gasto no mes"
              />

              {detalhe.data && (
                <div style={{ marginTop: 'var(--space-6)' }}>
                  <div className="between" style={{ marginBottom: 'var(--space-3)' }}>
                    <h3 className="card-title" style={{ margin: 0 }}>
                      {fatia
                        ? `Para quem foi: ${nomeDaFatia(data.topCategories, fatia)}`
                        : 'Para quem foi, no mes inteiro'}
                    </h3>
                    {fatia && (
                      <button className="btn ghost" onClick={() => setFatia(null)}>
                        Ver todas
                      </button>
                    )}
                  </div>

                  {detalhe.data.payees.length === 0 ? (
                    <p className="muted">Nenhuma saida aqui.</p>
                  ) : (
                    <div className="table-scroll">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Quem recebeu</th>
                            <th>Categoria</th>
                            <th style={{ textAlign: 'right' }}>Vezes</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalhe.data.payees.map((payee) => (
                            <tr key={`${payee.name}-${payee.document ?? ''}`}>
                              <td>
                                <span className="payee-name">
                                  <span>{payee.name}</span>
                                  {payee.document && (
                                    <span className="payee-meta">{payee.document}</span>
                                  )}
                                </span>
                              </td>
                              <td>
                                <CategoryTag
                                  name={payee.categoryName}
                                  colorToken={payee.colorToken}
                                />
                              </td>
                              <td className="tabular" style={{ textAlign: 'right' }}>
                                {payee.count}
                              </td>
                              <td className="money" style={{ textAlign: 'right' }}>
                                {money(payee.totalCents)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </section>

            {data.openInvoices.length > 0 && (
              <section className="card">
                <h2 className="card-title">Faturas a pagar</h2>
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Cartao</th>
                        <th>Vencimento</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.openInvoices.map((invoice) => (
                        <tr
                          key={invoice.id}
                          className="row-link"
                          onClick={() => navegar('/contas')}
                        >
                          <td>{invoice.accountName}</td>
                          <td className="tabular">{dateBR(invoice.dueDate)}</td>
                          <td className="money" style={{ textAlign: 'right' }}>
                            {money(invoice.totalCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Link to="/contas" style={{ fontSize: 'var(--text-sm)' }}>
                  Pagar em Contas
                </Link>
              </section>
            )}

            <section className="card">
              <h2 className="card-title">Ultimos lancamentos</h2>

              {data.recentTransactions.length === 0 ? (
                <p className="card-hint" style={{ marginBottom: 0 }}>
                  Nada lancado ainda. Comece por <Link to="/contas">cadastrar uma conta</Link> e
                  depois registre o primeiro gasto em <Link to="/lancamentos">Lancamentos</Link>.
                </p>
              ) : (
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Descricao</th>
                        <th>Conta</th>
                        <th style={{ textAlign: 'right' }}>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentTransactions.map((transaction) => (
                        <tr
                          key={transaction.id}
                          className="row-link"
                          title="Abrir em Lancamentos"
                          onClick={() => navegar(`/lancamentos?accountId=${transaction.accountId}`)}
                        >
                          <td className="tabular">{dateBR(transaction.date)}</td>
                          <td>
                            {transaction.description}
                            {transaction.categoryPath && (
                              <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                                {transaction.categoryPath}
                              </div>
                            )}
                          </td>
                          <td className="muted">{transaction.accountName}</td>
                          <td
                            className={`money ${amountClass(transaction.amountCents)}`}
                            style={{ textAlign: 'right' }}
                          >
                            {BigInt(transaction.amountCents) < 0n ? '-' : '+'}
                            {absMoney(transaction.amountCents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {!user?.totpEnabled && (
        <div className="alert warning" style={{ marginTop: 'var(--space-6)' }}>
          A verificacao em duas etapas ainda nao esta ativa.{' '}
          <Link to="/seguranca">Ativar agora</Link>.
        </div>
      )}
    </div>
  );
}

/**
 * Um cartao do topo, que leva a algum lugar.
 *
 * E um `Link` e nao um `div` com `onClick`: assim o navegador sabe que e
 * navegacao — abre em nova aba com o botao do meio, entra no historico, e o
 * teclado alcanca sem nenhuma linha a mais.
 */
function Cartao({
  rotulo,
  para,
  destaque = false,
  children,
}: {
  rotulo: string;
  para: string;
  /** O cartao por onde a leitura comeca. So um deles tem. */
  destaque?: boolean;
  children: ReactNode;
}) {
  return (
    <Link to={para} className={`stat stat-link${destaque ? ' destaque' : ''}`}>
      <span className="stat-label">{rotulo}</span>
      <span style={{ display: 'block' }}>{children}</span>
    </Link>
  );
}

function nomeDaFatia(
  categorias: Array<{ categoryId: string | null; name: string }>,
  key: string,
): string {
  return categorias.find((c) => (c.categoryId ?? c.name) === key)?.name ?? 'categoria';
}

function saudacao(): string {
  const hora = Number(
    new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: 'numeric',
      hour12: false,
    }).format(new Date()),
  );

  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}
